'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var jsxRuntime = require('react/jsx-runtime');
var React = require('react');
var dynamic$1 = require('next/dynamic');

function _interopDefault (e) { return e && e.__esModule ? e : { default: e }; }

var React__default = /*#__PURE__*/_interopDefault(React);
var dynamic__default = /*#__PURE__*/_interopDefault(dynamic$1);

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

function useRemoteModule(loader) {
    const [Module, setModule] = React__default.default.useState();
    const [error, setError] = React__default.default.useState();
    const [status, setStatus] = React__default.default.useState('loading');
    React__default.default.useEffect(() => {
        if (status !== 'loading') {
            setStatus('loading');
        }
        const loaderPromise = loader instanceof Function ? loader() : loader;
        const remoteModulePromise = loaderPromise
            .then((module) => {
            // Give suspense some buffer to get the returned module ready before setting the ready flag
            // testing showed that this led to fewer content flashing
            setTimeout(() => {
                setStatus('success');
            });
            if ('default' in module) {
                return { default: module.default };
            }
            return { default: module };
        })
            .catch((error) => {
            setError(error);
            setStatus('error');
            return { default: () => null };
        });
        setModule(remoteModulePromise);
    }, [loader]);
    if (status === 'success' && Module) {
        return { status, Module };
    }
    if (status === 'error' && error) {
        return { status, error };
    }
    return { status: 'loading' };
}
function createCSRComponent(loader) {
    function FrontierCSRRemote(props, ref) {
        var _a, _b;
        const { loading, error } = props, remoteComponentProps = __rest(props, ["loading", "error"]);
        const remote = useRemoteModule(loader);
        const [Component, setComponent] = React__default.default.useState();
        React__default.default.useEffect(() => {
            if (remote.status !== 'success') {
                return;
            }
            setComponent(React__default.default.lazy(() => __awaiter(this, void 0, void 0, function* () { return remote.Module; })));
        }, [remote.status]);
        if (remote.status === 'error') {
            if (!error) {
                console.error(`[frontier/dynamic]: Failed to import CSR'd remote module.`, error);
                return null;
            }
            return error(remote.error);
        }
        if (remote.status === 'loading' || Component === undefined) {
            return (_a = loading === null || loading === void 0 ? void 0 : loading()) !== null && _a !== void 0 ? _a : null;
        }
        return (jsxRuntime.jsx(React__default.default.Suspense, { fallback: (_b = loading === null || loading === void 0 ? void 0 : loading()) !== null && _b !== void 0 ? _b : null, children: jsxRuntime.jsx(Component, Object.assign({ ref: ref }, remoteComponentProps)) }));
    }
    FrontierCSRRemote.displayName = 'FrontierCSRRemote';
    return React__default.default.forwardRef(FrontierCSRRemote);
}

const LoadableContext = React__default.default.createContext(null);
if (process.env.NODE_ENV !== 'production') {
    LoadableContext.displayName = 'DynamicImportRemoteLoadableContext';
}

function isNodeEnv() {
    return typeof window === 'undefined';
}

const IMPORT_REMOTE_SINGLETON_NAMESPACE = 'spt.importRemoteSingleton';
/**
 * Returns the global import remote context
 * @returns {Object | undefined} AsyncLocalstorage or undefined
 */
/* istanbul ignore next */
function getImportRemoteContext() {
    if (isNodeEnv()) {
        return global[Symbol.for(IMPORT_REMOTE_SINGLETON_NAMESPACE)];
    }
    return undefined;
}
/**
 * Fetch imprort remote relevant data from importRemoteContext
 * @param {String} moduleName
 * @returns {Object} { env, hasOverrideCookie, versionOverride }
 */
function getDataFromRemoteContext(moduleName) {
    const importRemoteContext = getImportRemoteContext();
    if (typeof importRemoteContext !== 'undefined') {
        const { getModuleVersionOverride, getVersionOverrides, env, pushExecutionContext, popExecutionContext, pushDynamicExecutionContext, popDynamicExecutionContext, pushManifestContext, setDynamicExecutionContextSnapshot, } = importRemoteContext;
        const hasOverrideCookie = moduleName && Boolean(getVersionOverrides === null || getVersionOverrides === void 0 ? void 0 : getVersionOverrides());
        const versionOverride = moduleName && hasOverrideCookie
            ? getModuleVersionOverride === null || getModuleVersionOverride === void 0 ? void 0 : getModuleVersionOverride(moduleName)
            : undefined;
        return {
            env,
            hasOverrideCookie,
            versionOverride,
            pushExecutionContext,
            popExecutionContext,
            pushDynamicExecutionContext,
            popDynamicExecutionContext,
            pushManifestContext,
            setDynamicExecutionContextSnapshot,
        };
    }
    return {};
}
/** doing deep copy to prevent accidental update outside its use */
function getDynamicExecutionContext() {
    var _a;
    const importRemoteContext = getImportRemoteContext();
    const result = deepCopy(((_a = importRemoteContext === null || importRemoteContext === void 0 ? void 0 : importRemoteContext.getDynamicExecutionContext) === null || _a === void 0 ? void 0 : _a.call(importRemoteContext)) || []);
    return result;
}
/** doing deep copy to prevent accidental update outside its use */
function getExecutionContext() {
    var _a;
    const importRemoteContext = getImportRemoteContext();
    const result = deepCopy(((_a = importRemoteContext === null || importRemoteContext === void 0 ? void 0 : importRemoteContext.getExecutionContext) === null || _a === void 0 ? void 0 : _a.call(importRemoteContext)) || []);
    return result;
}
/* eslint-disable @typescript-eslint/no-explicit-any */
function deepCopy(json) {
    return JSON.parse(JSON.stringify(json));
}

function createSSRComponent(loader, options) {
    var _a, _b;
    const { type, module, webpack } = options;
    const context = getDataFromRemoteContext();
    (_a = context.pushDynamicExecutionContext) === null || _a === void 0 ? void 0 : _a.call(context, {
        module,
        importRemoteContext: getExecutionContext(),
    });
    const dynamicExecutionContextSnapshot = getDynamicExecutionContext();
    const wrappedLoader = () => __awaiter(this, void 0, void 0, function* () {
        var _a;
        (_a = context.setDynamicExecutionContextSnapshot) === null || _a === void 0 ? void 0 : _a.call(context, dynamicExecutionContextSnapshot);
        if (loader instanceof Function) {
            return yield loader();
        }
        return loader;
    });
    // Wrapping LoadableComponent into our own component so we can keep track of both import and importRemote component
    // renders and have the module id's available
    const Component = dynamic__default.default(wrappedLoader, { webpack });
    function FrontierSSRRemote(props, ref) {
        // Loadable context is setup at the _app level in Next.js service
        // This tracks which modules are rendered so we can load them on the browser
        const context = React__default.default.useContext(LoadableContext);
        if (context) {
            context({ type, module });
        }
        return React__default.default.createElement(Component, Object.assign({ ref }, props));
    }
    FrontierSSRRemote.displayName = 'FrontierSSRRemote';
    try {
        return React__default.default.forwardRef(FrontierSSRRemote);
    }
    finally {
        (_b = context.popDynamicExecutionContext) === null || _b === void 0 ? void 0 : _b.call(context);
    }
}

// 'type' option validation
const OPTION_TYPE = ['remote', 'local'];
const OPTION_TYPE_ERROR = `[frontier/dynamic]: Option 'type' must be '${OPTION_TYPE[0]}' or '${OPTION_TYPE[1]}'.`;
function isOptionType(optionType) {
    if (typeof optionType !== 'string') {
        throw new Error(OPTION_TYPE_ERROR);
    }
    if (!OPTION_TYPE.includes(optionType)) {
        throw new Error(OPTION_TYPE_ERROR);
    }
}
// 'render' option validation
const OPTION_RENDER = ['ssr', 'csr'];
const OPTION_RENDER_ERROR = `[frontier/dynamic]: Option 'render' must be '${OPTION_RENDER[0]}' or '${OPTION_RENDER[1]}'.`;
function isOptionRender(optionRender) {
    if (typeof optionRender !== 'string') {
        throw new Error(OPTION_RENDER_ERROR);
    }
    if (!OPTION_RENDER.includes(optionRender)) {
        throw new Error(OPTION_RENDER_ERROR);
    }
}
// 'module' option validation
const OPTION_MODULE_ERROR = `[frontier/dynamic]: Option 'module' must be of type 'string'.`;
function isOptionModule(optionModule) {
    if (typeof optionModule !== 'string') {
        throw new Error(OPTION_MODULE_ERROR);
    }
}
// 'webpack' option validation
const OPTION_WEBPACK_ERROR = `[frontier/dynamic]: Option 'webpack' must be of type 'function'.`;
function isOptionWebpack(optionWebpack) {
    if (!(optionWebpack instanceof Function)) {
        throw new Error(OPTION_WEBPACK_ERROR);
    }
}
function isOptions(options) {
    if (!options || typeof options !== 'object') {
        throw new Error(`[frontier/dynamic]: Options must be of type 'object'.`);
    }
    if (!('type' in options)) {
        throw new Error(`[frontier/dynamic]: Options must contain property 'type'.`);
    }
    isOptionType(options.type);
    if (!('render' in options)) {
        throw new Error(`[frontier/dynamic]: Options must contain property 'render'.`);
    }
    isOptionRender(options.render);
    if (!('module' in options)) {
        throw new Error(`[frontier/dynamic]: Options must contain property 'module'.`);
    }
    isOptionModule(options.module);
    if ('webpack' in options) {
        isOptionWebpack(options.webpack);
    }
}
function isOptionsCSR(options) {
    if (options.type === 'remote' &&
        options.render === 'csr' &&
        options.webpack === undefined) {
        return true;
    }
    return false;
}
function isOptionsSSR(options) {
    if (options.type === 'remote' &&
        options.render === 'ssr' &&
        options.webpack) {
        return true;
    }
    return false;
}
function isOptionsDynamicImport(options) {
    if (options.type === 'local' &&
        options.render === 'ssr' &&
        options.webpack) {
        return true;
    }
    return false;
}

/**
 * NOTE:
 * Disabling @typescript-eslint/no-explicit-any rule to default
 * dynamic Ref generic type to any an be able to defined the type of
 * the returned component props without needing to spicify the element
 * ref type.
 *
 * EXAMPLE:
 *
 * const Component = dynamic<ComponentProps>(() => remote('someRemote/SomeRemote));
 *
 */
/**
 * The function `dynamic` in TypeScript React dynamically loads components based on specified options.
 * @param loader - The `loader` parameter in the `dynamic` function is a function that loads the
 * component dynamically. It is used to lazily load components in your application.
 * @param {DynamicOptions} [options] - The `options` parameter in the `dynamic` function is an optional
 * parameter that allows you to specify additional configuration options for the dynamic component
 * loading. It is of type `DynamicOptions`, which contains properties related to server-side
 * rendering (SSR), client-side rendering (CSR), and dynamic imports.
 * @returns The `dynamic` function returns either a server-side rendered component, a client-side
 * rendered component, or throws an error if the options are invalid.
 */
function dynamic(loader, options) {
    if (!loader || !(loader instanceof Function)) {
        throw new Error(`[frontier/dynamic]: Parameter 'loader' must be a of type 'function'.`);
    }
    isOptions(options);
    const usingSSR = isOptionsSSR(options);
    const usingDynamicImport = isOptionsDynamicImport(options);
    if (usingSSR || usingDynamicImport) {
        return createSSRComponent(loader, options);
    }
    const usingCSR = isOptionsCSR(options);
    if (usingCSR) {
        return createCSRComponent(loader);
    }
    throw new Error(`[frontier/dynamic]: Invalid parameter 'options'.`);
}

exports.DynamicImportRemoteLoadableContext = LoadableContext;
exports.dynamic = dynamic;
